import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { UserRound, GraduationCap, Mail, Lock, User } from "lucide-react";
import { http } from "@/lib/api";
import { savePortalSession, startGoogleLogin } from "@/lib/portalAuth";

export default function PortalLogin({ portalType }) {
  const nav = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const isStudent = portalType === "student";
  const Icon = isStudent ? GraduationCap : UserRound;
  const title = isStudent ? "Study abroad journey" : "Track your visa";
  const label = isStudent ? "Student portal" : "Applicant portal";
  const dashRoute = isStudent ? "/student" : "/applicant";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "signup" ? "/portal/signup" : "/portal/login";
      const payload = mode === "signup"
        ? { email, password, name, portal_type: portalType }
        : { email, password };
      const { data } = await http.post(url, payload);
      savePortalSession(data);
      toast.success(mode === "signup" ? "Account created — welcome!" : `Welcome back, ${data.name || data.email}`);
      nav(dashRoute);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`${portalType}-login-page`} className="min-h-screen flex items-center justify-center px-4 stardust relative py-10">
      <div className="relative z-10 w-full max-w-md glass-dark rounded-3xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#C9A961]/15 text-[#C9A961] flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">{label}</div>
            <h1 className="font-serif text-2xl">{title}</h1>
          </div>
        </div>

        <button
          data-testid={`${portalType}-google-signin`}
          onClick={() => startGoogleLogin(portalType)}
          className="w-full rounded-xl bg-white hover:bg-slate-100 text-slate-800 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-slate-200"
        >
          <GoogleG />
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5 text-[11px] uppercase tracking-widest text-slate-500">
          <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
        </div>

        <div className="flex gap-2 mb-4">
          {[["signin", "Sign in"], ["signup", "Create account"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`${portalType}-tab-${k}`}
              onClick={() => setMode(k)}
              className={`flex-1 rounded-xl py-2 text-xs uppercase tracking-widest font-semibold border transition ${
                mode === k ? "bg-[#C9A961] text-[#0B1E3F] border-[#C9A961]" : "bg-transparent text-slate-300 border-white/15 hover:border-[#C9A961]/60"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <Field icon={User} testId={`${portalType}-name`} type="text" placeholder="Full name" value={name} onChange={setName} required />
          )}
          <Field icon={Mail} testId={`${portalType}-email`} type="email" placeholder="Email" value={email} onChange={setEmail} required />
          <Field icon={Lock} testId={`${portalType}-password`} type="password" placeholder="Password (min 6 chars)" value={password} onChange={setPassword} required minLength={6} />
          <button
            type="submit"
            disabled={busy}
            data-testid={`${portalType}-submit`}
            className="btn-gold w-full rounded-xl py-3 font-semibold disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-[11px] text-slate-500 text-center">
          <Link to="/" className="underline text-[#C9A961]">Back to NextShore</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testId, value, onChange, ...rest }) {
  return (
    <label className="block relative">
      <Icon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#162A4D]/60 border border-[#C9A961]/25 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-[#C9A961]/70"
        {...rest}
      />
    </label>
  );
}

function GoogleG() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.1 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 7 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 7 29 5 24 5 16.3 5 9.6 9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43c5 0 9.6-1.9 13-5l-6-5.1c-2 1.3-4.4 2.1-7 2.1-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 38.9 16.2 43 24 43z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.8l6 5.1c-.4.4 6.4-4.7 6.4-14.9 0-1.2-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
